import { ChangeEvent, ComponentProps, useId } from 'react'
import { useTranslations } from 'next-intl'
import { cn } from '@/lib/utils'
import { Search, X } from 'lucide-react'
import { Button } from '@/app/[locale]/(ui)/button'

export const Input = ({
  value = '',
  type = 'text',
  className,
  label = '',
  error = '',
  required,
  onChange,
  ...props
}: {
  label?: string
  error?: string
  value?: string
} & Omit<ComponentProps<'input'>, 'value'>) => {
  const t = useTranslations()
  const id = useId()
  const isTypeSearch = type === 'search'
  const hasValue = value.length > 0

  const onClear = () => {
    onChange?.({
      target: { value: '' },
      currentTarget: { value: '' },
      nativeEvent: {},
    } as ChangeEvent<HTMLInputElement>)
  }

  return (
    <div className={cn('w-full', className)}>
      {label && (
        <label htmlFor={id}>
          {label}
          {required && '*'}
        </label>
      )}
      <div className="relative">
        {isTypeSearch && <Search size={18} className="absolute bottom-[.75em] left-3" />}
        <input
          id={id}
          type={type}
          autoComplete="off"
          inputMode={type === 'search' ? 'search' : 'text'}
          spellCheck="false"
          className={cn(
            'w-full appearance-none rounded-md border border-black bg-white px-4 py-2 placeholder:text-neutral-500 disabled:cursor-not-allowed disabled:opacity-50',
            { 'border-red-700': !!error, 'px-9': isTypeSearch },
          )}
          value={value}
          onChange={onChange}
          {...props}
        />
        {isTypeSearch && hasValue && (
          <Button
            variant="tertiary"
            shape="square"
            aria-label={t('Clear_field')}
            className="absolute bottom-[.47em] right-2"
            onClick={onClear}
          >
            <X size={18} />
          </Button>
        )}
      </div>
      {error && <div className="text-sm text-red-700">{error}</div>}
    </div>
  )
}
